import type {
  CreateEventRequest,
  CreatePartRequest,
  EventOptions,
  EventPart,
  RequestBody,
  UpdateEventRequest,
  UpdatePartRequest,
} from "@repo/shared-types";
import {
  LENGTH_UNITS,
  durationFromBoxes,
  fromWallTime,
  joinWall,
  toWallTime,
  wallAfter,
  type LengthUnit,
} from "./times";

/**
 * Reading the event forms into what the api takes.
 *
 * Times arrive as a person typed them, in their own zone, and leave as
 * moments. A time limit arrives in minutes and seconds boxes and leaves as
 * milliseconds, and is dropped when the part's score types take none — a form
 * keeps a field's value after somebody switches to a type that hides it.
 *
 * The api checks all of it again; checking here is only so the message names
 * the part and the score type the way the form did.
 */

/**
 * What a reader made of a form: the value, or why not — and, when one box is
 * the reason, its name, so the form outlines it and says why under it.
 */
export type Read<T> = { value: T; error?: undefined; field?: undefined } | { value?: undefined; error: string; field?: string };

/** How the form names a part: A, B, C… and then by number. */
export function partLabel(index: number): string {
  return index < 26 ? String.fromCharCode(65 + index) : String(index + 1);
}

const text = (form: FormData, name: string) => String(form.get(name) ?? "").trim();

/** What the publish radio buttons send. */
export type PublishChoice = "now" | "at" | "draft";

/**
 * How an event may rank its athletes across its parts. Only a format that
 * allows it — the list says which — offers anything but "none", and the
 * choice is fixed once the event is posted.
 */
export const RANKINGS = [
  { value: "none", label: "None", hint: "each part ranked on its own" },
  { value: "sum", label: "Sum", hint: "one total across the parts that measure the same thing" },
  { value: "placement", label: "Placement points", hint: "a place on each part is its points; fewest wins" },
] as const;

export type Ranking = (typeof RANKINGS)[number]["value"];

/** A ranking as a person reads it. The set grows by addition: an unknown one shows as it came. */
export function rankingLabel(ranking: string): string {
  return RANKINGS.find((option) => option.value === ranking)?.label ?? ranking;
}

/**
 * Whether events of a format are posts (0090): no sport, no parts, no dates of
 * their own. The format's row says so, never its label.
 */
export function isPost(options: EventOptions, formatId: string): boolean {
  return options.formats.find((format) => format.id === formatId)?.isPost ?? false;
}

type EventFields = {
  title: string;
  formatId: string;
  description: string;
  publish: { choice: "now" } | { choice: "at"; at: string } | { choice: "draft" };
  // Left out on a post (0090).
  sportId?: string;
  startsAt?: string;
  endsAt?: string;
};

/** A date box and a time box named `${name}.date` and `${name}.time`, as one wall time, or null. */
export function wallOf(form: FormData, name: string): string | null {
  return joinWall(text(form, `${name}.date`), text(form, `${name}.time`));
}

/** The most of a unit an event's length may be: 999 days is nearly three years. */
export const LONGEST = 999;

/**
 * When the event starts and ends: Starts' date and time boxes, and Ends — a
 * length after Starts (`endsAt.amount` of `endsAt.unit`), or with
 * `endsAt.custom` its own date and time boxes (0088). As wall times in
 * `zone`, which a part's sheet on New event is bounded by, and as the moments
 * the reader sends.
 */
export function readEventDates(
  form: FormData,
  zone: string,
): Read<{ starts: string; ends: string; startsAt: string; endsAt: string }> {
  const starts = wallOf(form, "startsAt");
  const startsAt = starts && fromWallTime(starts, zone);
  if (!starts || !startsAt) return { error: "Say when the event starts.", field: "startsAt" };

  let ends: string | null;
  if (form.get("endsAt.custom") !== null) {
    ends = wallOf(form, "endsAt");
    if (!ends) return { error: "Say when the event ends.", field: "endsAt" };
  } else {
    const amount = text(form, "endsAt.amount");
    const unit = text(form, "endsAt.unit") as LengthUnit;
    if (!/^\d+$/.test(amount) || Number(amount) < 1 || Number(amount) > LONGEST || !LENGTH_UNITS.includes(unit)) {
      return { error: `Say how long it lasts: a whole number from 1 to ${LONGEST}.`, field: "endsAt" };
    }
    ends = wallAfter(starts, Number(amount), unit, zone);
  }
  const endsAt = ends && fromWallTime(ends, zone);
  if (!ends || !endsAt) return { error: "Say when the event ends.", field: "endsAt" };
  if (Date.parse(endsAt) <= Date.parse(startsAt)) {
    return { error: "The event ends after it starts.", field: "endsAt" };
  }
  return { value: { starts, ends, startsAt, endsAt } };
}

/**
 * What every event form sends. A post sends no sport and no dates (0090): its
 * start is when it goes out, and the form hides — and disables — their boxes.
 */
function readEventFields(form: FormData, zone: string, options: EventOptions): Read<EventFields> {
  const title = text(form, "title");
  if (!title) return { error: "Give the event a title.", field: "title" };

  const formatId = text(form, "formatId");
  const post = isPost(options, formatId);
  let when: { sportId: string; startsAt: string; endsAt: string } | null = null;
  if (!post) {
    const dates = readEventDates(form, zone);
    if (dates.error !== undefined) return dates;
    when = { sportId: text(form, "sportId"), startsAt: dates.value.startsAt, endsAt: dates.value.endsAt };
  }

  const choice = text(form, "publish") as PublishChoice;
  let publish: EventFields["publish"];
  if (choice === "now") {
    publish = { choice: "now" };
  } else if (choice === "at") {
    const wall = wallOf(form, "publishAt");
    const at = wall && fromWallTime(wall, zone);
    if (!at) return { error: "Say when to publish it.", field: "publishAt" };
    publish = { choice: "at", at };
  } else {
    publish = { choice: "draft" };
  }

  return {
    value: {
      title,
      formatId,
      description: text(form, "description"),
      publish,
      ...when,
    },
  };
}

/** The new-event form, parts included. */
export function readNewEvent(
  form: FormData,
  zone: string,
  options: EventOptions,
): Read<RequestBody<CreateEventRequest>> {
  const fields = readEventFields(form, zone, options);
  if (fields.error !== undefined) return fields;
  // A part's own boxes are its sheet's, checked there: here it is a card. A
  // post has none, whatever cards wait hidden for a switch back (0090).
  const parts = isPost(options, fields.value.formatId) ? { value: [] } : readParts(form, zone, options);
  if (parts.error !== undefined) return { error: parts.error };

  const { publish, description, endsAt, ...rest } = fields.value;
  // Sent only by a format that allows an overall ranking: the form shows the
  // choice for no other.
  const ranking = text(form, "ranking");
  const allowed = options.formats.find((format) => format.id === rest.formatId)?.allowsOverall ?? false;
  return {
    value: {
      ...rest,
      ...(description ? { description } : {}),
      ...(endsAt ? { endsAt } : {}),
      ...(allowed && ranking && ranking !== "none" ? { ranking } : {}),
      ...(publish.choice === "now" ? { publishNow: true } : {}),
      ...(publish.choice === "at" ? { publishedAt: publish.at } : {}),
      parts: parts.value,
    },
  };
}

/**
 * The edit form. It always sends every field, so "" is how an emptied
 * description is cleared, and choosing "draft" takes it back. It always has
 * an end, too: the form saves no event without one (0088) — but a post, which
 * sends no sport and no dates (0090).
 * `fitParts` is there when it is sent again from the sheet listing the parts
 * its new dates leave outside, to move them inside with it.
 */
export function readEventChanges(
  form: FormData,
  zone: string,
  options: EventOptions,
): Read<RequestBody<UpdateEventRequest>> {
  const fields = readEventFields(form, zone, options);
  if (fields.error !== undefined) return fields;

  const { publish, ...rest } = fields.value;
  return {
    value: {
      ...rest,
      ...(form.get("fitParts") === "true" ? { fitParts: true } : {}),
      // Publishing what is already published changes nothing, so "now" is
      // safe to send for an event that is.
      ...(publish.choice === "now" ? { publishNow: true } : {}),
      ...(publish.choice === "at" ? { publishedAt: publish.at } : {}),
      ...(publish.choice === "draft" ? { publishedAt: "" } : {}),
    },
  };
}

/** The indices the form used under a prefix, in the order it sent them. */
export function indices(form: FormData, pattern: RegExp): string[] {
  const found: string[] = [];
  for (const key of form.keys()) {
    const match = pattern.exec(key);
    if (match?.[1] !== undefined && !found.includes(match[1])) found.push(match[1]);
  }
  return found;
}

/**
 * Every part in the form, named parts.0.title, parts.0.timeLimit.min and .sec,
 * parts.0.levels and parts.0.leaderboard (checkboxes), parts.0.deadline and
 * parts.0.verifiable (only there while the part has a leaderboard),
 * parts.0.scores.0.type (a score type's id) and so on — a part's sheet
 * sends exactly one.
 *
 * Whether the time limit is needed, allowed or refused is the score types
 * list's to say: the options carry it for each type. Whether a deadline has
 * already passed is the api's: it refuses one in the past by its own clock.
 */
export function readParts(
  form: FormData,
  zone: string,
  options: EventOptions,
  /** Where the first part in the form sits among the event's, so a message names it: a part's sheet sends one. */
  first = 0,
): Read<CreatePartRequest[]> {
  const parts: CreatePartRequest[] = [];
  for (const [position, index] of indices(form, /^parts\.(\d+)\.title$/).entries()) {
    const prefix = `parts.${index}`;
    const name = `Part ${partLabel(first + position)}`;

    const title = text(form, `${prefix}.title`);
    if (!title) return { error: `${name} needs a title.`, field: `${prefix}.title` };

    const startsRaw = text(form, `${prefix}.startsAt`);
    const startsAt = startsRaw ? fromWallTime(startsRaw, zone) : null;
    if (startsRaw && !startsAt) return { error: `${name}'s start is not a time.`, field: `${prefix}.startsAt` };

    const types = indices(form, new RegExp(`^parts\\.${index}\\.scores\\.(\\d+)\\.type$`)).map(
      (scoreIndex) => text(form, `${prefix}.scores.${scoreIndex}.type`),
    );
    if (types.length === 0) return { error: `Say how ${name} is scored.`, field: `${prefix}.scores` };

    const limit = readTimeLimit(form, `${prefix}.timeLimit`, types, name, options);
    if (limit.error !== undefined) return limit;

    const deadlineRaw = text(form, `${prefix}.deadline`);
    const deadline = deadlineRaw ? fromWallTime(deadlineRaw, zone) : null;
    if (deadlineRaw && !deadline) return { error: `${name}'s deadline is not a time.`, field: `${prefix}.deadline` };

    parts.push({
      title,
      description: text(form, `${prefix}.description`) || null,
      startsAt,
      timeLimitMs: limit.value,
      // A checkbox sends nothing when it is not ticked, and the form shows
      // none for a part that is not scored: either way, no levels.
      usesLevels: form.get(`${prefix}.levels`) !== null,
      // The same: unticked, or not shown for a part that is not scored, it
      // has no leaderboard.
      hasLeaderboard: form.get(`${prefix}.leaderboard`) !== null,
      deadline,
      verifiable: form.get(`${prefix}.verifiable`) !== null,
      // Done in teams: ticked, or not at all.
      isTeam: form.get(`${prefix}.team`) !== null,
      scores: types.map((scoreTypeId) => ({ scoreTypeId })),
    });
  }
  return { value: parts };
}

/**
 * An event's own dates as wall times in the viewer's zone, "" for none: what
 * a part's dates live inside (0083). A part starts no earlier than the event,
 * and is due no earlier than it starts and no later than the event's
 * deadline — which, once it has passed (`passed`, from the api's phase), no
 * new part with a leaderboard can be due by.
 */
export type PartBounds = { starts: string; deadline: string; passed?: boolean };

/**
 * A part's sheet against its event's dates, before it is sent. Wall times in
 * one zone, "YYYY-MM-DDTHH:MM", so they compare as text. The api checks again,
 * by moments.
 */
export function checkPartDates(form: FormData, bounds: PartBounds, index: number, prefix = SHEET_PREFIX): Read<null> {
  const name = `Part ${partLabel(index)}`;
  // Said above the sheet: the leaderboard's chip is no box to outline.
  if (bounds.passed && form.get(`${prefix}.leaderboard`) !== null) {
    return { error: "The event's deadline has passed: move it later to add a part with a leaderboard." };
  }
  const starts = text(form, `${prefix}.startsAt`);
  const deadline = text(form, `${prefix}.deadline`);
  if (starts && bounds.starts && starts < bounds.starts) {
    return { error: `${name} starts no earlier than the event.`, field: `${prefix}.startsAt` };
  }
  if (deadline && bounds.deadline && deadline > bounds.deadline) {
    return { error: `${name} is due no later than the event's deadline.`, field: `${prefix}.deadline` };
  }
  const begins = starts || bounds.starts;
  if (deadline && begins && deadline < begins) {
    // The part's own start, where it has one, is what moved past its deadline.
    return starts
      ? { error: `${name} starts no later than it is due.`, field: `${prefix}.startsAt` }
      : { error: `${name} is due no earlier than it starts.`, field: `${prefix}.deadline` };
  }
  return { value: null };
}

/** The event's dates as a sheet over a posted event bounds its new part. */
export function boundsOfEvent(
  event: { startsAt: string; endsAt: string | null; phase?: string | null },
  zone: string,
): PartBounds {
  return {
    starts: toWallTime(event.startsAt, zone),
    deadline: event.endsAt ? toWallTime(event.endsAt, zone) : "",
    // An event with a deadline has ended once that deadline has passed, by the api's clock.
    passed: event.endsAt !== null && event.phase === "ended",
  };
}

/**
 * A part as its sheet sent it, kept on the page until the form around it is
 * sent: each name without the sheet's own `parts.0.` — "title",
 * "scores.0.type", "timeLimit.min" — in the order the sheet sent them. The
 * page names them again under the part's place, which is what readParts
 * reads, so a part kept this way is read exactly as one typed in place.
 */
export type PartFields = [string, string][];

/** What a part's sheet names its fields under: one part, the first. */
export const SHEET_PREFIX = "parts.0";

/** A part's fields out of its sheet's form. */
export function fieldsOf(form: FormData, prefix = SHEET_PREFIX): PartFields {
  const fields: PartFields = [];
  for (const [name, value] of form.entries()) {
    if (typeof value === "string" && name.startsWith(`${prefix}.`)) fields.push([name.slice(prefix.length + 1), value]);
  }
  return fields;
}

/** A part's fields as a form names them under `prefix`: what readParts reads. */
export function formOf(fields: PartFields, prefix = SHEET_PREFIX): FormData {
  const form = new FormData();
  for (const [name, value] of fields) form.append(`${prefix}.${name}`, value);
  return form;
}

/**
 * How a part is scored as a person reads it — the api's `summary`, worked
 * out here for a part kept on the page before it is sent: "For time, 10 min
 * cap, then for reps", "AMRAP, 12 min", "For load, in 5 min". From what each
 * type measures and allows, never its name, as the api does.
 */
export function partSummary(
  scores: { scoreTypeId: string }[],
  timeLimitMs: number | null,
  options: EventOptions,
): string {
  const types = scores.flatMap(({ scoreTypeId }) => options.scoreTypes.filter((option) => option.id === scoreTypeId));
  const [first, ...tiebreaks] = types;
  if (!first) return "";
  let summary = first.label;
  if (timeLimitMs !== null) {
    const limit = limitLabel(timeLimitMs);
    // The limit is what the part is, an AMRAP's window; on a race against the
    // clock it is a cap.
    if (first.timeLimit === "required") summary += `, ${limit}`;
    else if (first.measure === "time" && first.better === "lower") summary += `, ${limit} cap`;
    else summary += `, in ${limit}`;
  }
  for (const tiebreak of tiebreaks) summary += `, then ${lowerFirst(tiebreak.label)}`;
  return summary;
}

/** Whole minutes as "12 min", anything else as "12:30". */
function limitLabel(ms: number): string {
  const seconds = Math.floor(ms / 1_000);
  if (seconds % 60 === 0) return `${seconds / 60} min`;
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/** "For reps" reads as "for reps" mid-sentence; "AMRAP" stays as it is. */
function lowerFirst(label: string): string {
  const [first = "", second = ""] = label;
  return first !== first.toLowerCase() && second === second.toLowerCase() ? first.toLowerCase() + label.slice(1) : label;
}

/**
 * A part kept on the page, read as the event's page shows a posted one — or
 * null, should it no longer read (its sheet checked it as it was saved).
 */
export function draftPart(fields: PartFields, zone: string, options: EventOptions): EventPart | null {
  const part = readParts(formOf(fields), zone, options).value?.[0];
  if (!part) return null;
  return {
    id: "",
    title: part.title,
    description: part.description,
    startsAt: part.startsAt,
    timeLimitMs: part.timeLimitMs,
    usesLevels: part.usesLevels ?? false,
    hasLeaderboard: part.hasLeaderboard ?? false,
    deadline: part.deadline,
    deadlinePassed: false,
    verifiable: part.verifiable ?? false,
    isTeam: part.isTeam ?? false,
    summary: partSummary(part.scores, part.timeLimitMs, options),
    viewerStanding: null,
    scores: part.scores.flatMap(({ scoreTypeId }) =>
      options.scoreTypes
        .filter((option) => option.id === scoreTypeId)
        .map(({ id, label, measure, better }) => ({ scoreTypeId: id, label, measure, better })),
    ),
  };
}

/**
 * How a part's score types, by id, treat its time limit: required when any of them
 * needs one (an AMRAP), refused when any takes none (a Tabata). A type this
 * build does not know is left for the api to judge.
 */
export function timeLimitRule(types: string[], options: EventOptions): "required" | "optional" | "none" {
  const rules = types.map((id) => options.scoreTypes.find((option) => option.id === id)?.timeLimit);
  if (rules.includes("none")) return "none";
  if (rules.includes("required")) return "required";
  return "optional";
}

function readTimeLimit(
  form: FormData,
  field: string,
  types: string[],
  part: string,
  options: EventOptions,
): Read<number | null> {
  const rule = timeLimitRule(types, options);
  if (rule === "none") return { value: null };
  const typed = durationFromBoxes(text(form, `${field}.min`), text(form, `${field}.sec`));
  if ("empty" in typed) {
    if (rule !== "required") return { value: null };
    const needs = options.scoreTypes.find(
      (option) => types.includes(option.id) && option.timeLimit === "required",
    );
    return { error: `${part}: ${needs?.label ?? "this score type"} needs a time limit, such as 12 min.`, field };
  }
  if ("error" in typed) return { error: `${part}: ${typed.error}.`, field };
  return { value: typed.value };
}

/**
 * A part's own edit form: its title, description and start, always all three
 * — and its deadline, when the form has the box, which it does only for a
 * part with a leaderboard. Emptied, the deadline goes.
 */
export function readPartChanges(
  form: FormData,
  zone: string,
): Read<RequestBody<UpdatePartRequest>> {
  const title = text(form, "title");
  if (!title) return { error: "A part needs a title.", field: "title" };
  const startsRaw = text(form, "startsAt");
  const startsAt = startsRaw ? fromWallTime(startsRaw, zone) : "";
  if (startsAt === null) return { error: "The start is not a time.", field: "startsAt" };
  if (form.get("deadline") === null) {
    return { value: { title, description: text(form, "description"), startsAt } };
  }
  const deadlineRaw = text(form, "deadline");
  const deadline = deadlineRaw ? fromWallTime(deadlineRaw, zone) : "";
  if (deadline === null) return { error: "The deadline is not a time.", field: "deadline" };
  return { value: { title, description: text(form, "description"), startsAt, deadline } };
}
